import { Observable, throwError as observableThrowError } from 'rxjs';
import { webSocket, WebSocketSubject } from 'rxjs/webSocket';
import { Inject, Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { CustomHttpParamCodec } from './custom-http-param-codec';
import isArray from 'lodash/isArray';
import clone from 'lodash/clone';
import extend from 'lodash/extend';

import { defaultHeaders } from '@lib/server-http';
import { getCookie, setCookie } from '@lib/cookies';
import { CookieKey } from '@lib/cookie-keys.const';
import { displayRequestDeniedAlert, inDemoMode, inReadOnlyMode, inSupportMode } from '@lib/remote-login';
import { DotIdObfuscationService } from '@lib/demo-mode/dot-id-obfuscation.service';

import { GroupsFilter } from './groups-filter';
import { tap } from 'rxjs/operators';

import { getApiUrl } from '@lib/environments.const';

const API_URL: string = getApiUrl();
export const W2_API_URL = `${API_URL}/api/w2`;
export const W2_WS_URL = `${API_URL.replace('http', 'ws')}/api/w2`;
export const W1_API = 'w1';
export const W2_API = 'w2';
export const W3_API = 'w3';
export const W4_API = 'w4';
export const W3_API_URL = `${API_URL}/api/${W3_API}`;
export const S1_API_URL = `${API_URL}/api/s1`;

// this is a BE param used to determine if they should check user permissions when retrieving assets, drivers, or vehicles
// necessary as the current permissions should only pertain to fleet view, but not during alerts workflow
export enum CALLER_NAMES {
  fleetview = 'fleetview',
  admin = 'entity-select-modal',
}

export enum HTTP_STATUS_CODE {
  Continue = 100,
  SwitchingProtocols = 101,
  Processing = 102,
  EarlyHints = 103,
  Ok = 200,
  Created = 201,
  Accepted = 202,
  NonAuthoritativeInformation = 203,
  NoContent = 204,
  ResetContent = 205,
  PartialContent = 206,
  MultiStatus = 207,
  AlreadyReported = 208,
  ImUsed = 226,
  MultipleChoices = 300,
  MovedPermanently = 301,
  Found = 302,
  SeeOther = 303,
  NotModified = 304,
  UseProxy = 305,
  Unused = 306,
  TemporaryRedirect = 307,
  PermanentRedirect = 308,
  BadRequest = 400,
  Unauthorized = 401,
  PaymentRequired = 402,
  Forbidden = 403,
  NotFound = 404,
  MethodNotAllowed = 405,
  NotAcceptable = 406,
  ProxyAuthenticationRequired = 407,
  RequestTimeout = 408,
  Conflict = 409,
  Gone = 410,
  LengthRequired = 411,
  PreconditionFailed = 412,
  PayloadTooLarge = 413,
  UriTooLong = 414,
  UnsupportedMediaType = 415,
  RangeNotSatisfiable = 416,
  ExpectationFailed = 417,
  ImATeapot = 418,
  MisdirectedRequest = 421,
  UnprocessableEntity = 422,
  Locked = 423,
  FailedDependency = 424,
  TooEarly = 425,
  UpgradeRequired = 426,
  PreconditionRequired = 428,
  TooManyRequests = 429,
  RequestHeaderFieldsTooLarge = 431,
  UnavailableForLegalReasons = 451,
  InternalServerError = 500,
  NotImplemented = 501,
  BadGateway = 502,
  ServiceUnavailable = 503,
  GatewayTimeout = 504,
  HttpVersionNotSupported = 505,
  VariantAlsoNegotiates = 506,
  InsufficientStorage = 507,
  LoopDetected = 508,
  NotExtended = 510,
  NetworkAuthenticationRequired = 511,
}

const GROUP_IDS_KEY = 'group_ids';
const SUPPORT_SESSION_ALLOWED_URLS: RegExp[] = [
  /^callbacks\/ready/,
  /^callbacks\/pusher\/join/,
  /^orders/,
  /^sessions\/payment_page/,
  /^billing\/address/,
  /^reports\//,
  /^reports_dna\/export/,
  /^analytics\/login_token/,
];
const DEMO_SESSION_ALLOWED_URLS: RegExp[] = [
  /^callbacks\/pusher\/join/,
  /^driver_performance\/events\/(\d)+\/update_blur/,
  /^video_recall_requests\/(\d)+\/update_blur/,
  /^analytics\/login_token/,
];

const READONLY_MODE_ALLOWED_URLS: RegExp[] = [
  /^callbacks\/ready/,
  /^callbacks\/pusher\/join/,
  /^orders/,
  /^sessions\/payment_page/,
  /^billing\/address/,
  /^reports\//,
  /^reports_dna\/export/,
  /^sessions\/switch_company/,
];

const HARD_VERSION_COOKIE_NAME: CookieKey = CookieKey.HARD_APP_VERSION;
let currentHardVersion = getCookie(HARD_VERSION_COOKIE_NAME);

@Injectable({ providedIn: 'root' })
export class ServerApi {
  public API_VER_W1: string = 'w1';
  public API_VER_W2: string = 'w2';
  public API_VER_W3: string = 'w3';
  public API_VER_W4: string = 'w4';
  public API_VER_W5: string = 'w5';
  private readonly requestDeniedErrorMessage: string = 'Cannot make non-GET requests in remote login session';
  private isReadonlyMode = false;

  constructor(
    private http: HttpClient,
    private zone: NgZone,
    @Inject(GroupsFilter) private groupsFilter,
    private customHttpParamCodec: CustomHttpParamCodec,
    private dotIdObfuscationService: DotIdObfuscationService,
  ) { }

  public get<T>(
    endpointUrl: string,
    params = new Map(),
    apiVer: string = this.API_VER_W2,
    headers = new Map(),
    useBracketsForArrayValues: boolean = true,
  ) {
    if (inDemoMode()) {
      this.decodeObfuscatedDotId(params);
    }
    return this.http
      .get<T>(
        `${this._getApiUrl(apiVer)}/${endpointUrl}`,
        this._getRequestOptions('get', params, headers, useBracketsForArrayValues),
      )
      .pipe(
        tap(this._checkVersion.bind(this)),
      );
  }

  public getBlob<T>(
    endpointUrl: string,
    params = new Map(),
    apiVer: string = this.API_VER_W2,
    headers = new Map(),
    useBracketsForArrayValues: boolean = true,
  ) {
    const options = this._getRequestOptions('get', params, headers, useBracketsForArrayValues);
    options.responseType = 'blob';
    return this.http
      .get<T>(
        `${this._getApiUrl(apiVer)}/${endpointUrl}`,
        options,
      )
      .pipe(
        tap(this._checkVersion.bind(this)),
      )
      ;
  }

  public postBlob<T>(
    endpointUrl: string,
    body: any,
    params = new Map(),
    apiVer: string = this.API_VER_W2,
    observeEvents: boolean = false,
  ): Observable<any> {
    if (!this.isRequestAllowed(endpointUrl)) {
      return this.requestDeniedError();
    }

    const options = this._getRequestOptions('post', params, new Map(), true, observeEvents);
    options.responseType = 'blob';

    return this.http
      .post<T>(
        `${this._getApiUrl(apiVer)}/${endpointUrl}`,
        JSON.stringify(body),
        options,
      ).pipe(
        tap(this._checkVersion.bind(this)),
      );
  }

  public getText<T>(
    endpointUrl: string,
    params = new Map(),
    apiVer: string = this.API_VER_W2,
    headers = new Map(),
    useBracketsForArrayValues: boolean = true,
  ) {
    const options = this._getRequestOptions('get', params, headers, useBracketsForArrayValues);
    options.responseType = 'text';
    return this.http
      .get<T>(
        `${this._getApiUrl(apiVer)}/${endpointUrl}`,
        options,
      )
      .pipe(
        tap(this._checkVersion.bind(this)),
      );
  }

  public post<T>(
    endpointUrl: string,
    body: any,
    params = new Map(),
    apiVer: string = this.API_VER_W2,
    observeEvents: boolean = false,
  ): Observable<any> {
    if (!this.isRequestAllowed(endpointUrl)) {
      return this.requestDeniedError();
    }

    return this.http
      .post<T>(
        `${this._getApiUrl(apiVer)}/${endpointUrl}`,
        JSON.stringify(body),
        this._getRequestOptions('post', params, new Map(), true, observeEvents),
      ).pipe(
        tap(this._checkVersion.bind(this)),
      );
  }

  public postRaw<T>(
    endpointUrl: string,
    body: any,
    params = new Map(),
    apiVer: string = this.API_VER_W2,
    observeEvents: boolean = false,
  ): Observable<any> {
    if (!this.isRequestAllowed(endpointUrl)) {
      return this.requestDeniedError();
    }

    const requestOptions = this._getRequestOptions('post', params, new Map(), true, observeEvents);
    // _getRequestOptions method sets the Content-Type as application/json for each call. We don't want to set it explicitly.
    const modifiedHeaders = requestOptions.headers.delete('Content-Type');
    requestOptions.headers = modifiedHeaders;

    return this.http
      .post<T>(
        `${this._getApiUrl(apiVer)}/${endpointUrl}`,
        body,
        requestOptions,
      ).pipe(
        tap(this._checkVersion.bind(this)),
      );
  }

  public publicPost<T>(endpointUrl: string, params = new Map(), apiVer: string, headers): Observable<any> {
    return this.http
      .post<T>(`${this._getApiUrl(apiVer)}/${endpointUrl}`, {}, this._getRequestOptions('post', params, headers))
      .pipe(
        tap(this._checkVersion.bind(this)),
      )
      ;
  }

  public put<T>(endpointUrl: string, body: any, params = new Map(), apiVer: string = this.API_VER_W2): Observable<any> {
    if (!this.isRequestAllowed(endpointUrl)) {
      return this.requestDeniedError();
    }

    return this.http
      .put<T>(`${this._getApiUrl(apiVer)}/${endpointUrl}`, JSON.stringify(body), this._getRequestOptions('put', params))
      .pipe(
        tap(this._checkVersion.bind(this)),
      )
      ;
  }

  public patch<T>(endpointUrl: string, body: any, params = new Map(), apiVer: string = this.API_VER_W2) {
    if (!this.isRequestAllowed(endpointUrl)) {
      return this.requestDeniedError();
    }

    return this.http
      .patch<T>(`${this._getApiUrl(apiVer)}/${endpointUrl}`, JSON.stringify(body), this._getRequestOptions('patch', params))
      .pipe(
        tap(this._checkVersion.bind(this)),
      )
      ;
  }

  public delete(endpointUrl, params: any = null, apiVer: string = this.API_VER_W2) {
    if (!this.isRequestAllowed(endpointUrl)) {
      return this.requestDeniedError();
    }
    return this.http
      .delete(`${this._getApiUrl(apiVer)}/${endpointUrl}`, {
        observe: 'response',
        params: this._getQueryParams(params),
        headers: this._getHeaders(),
      }).pipe(
        tap(this._checkVersion.bind(this)),
      );
  }

  public connectWebSocket(endpointUrl: string): WebSocketSubject<any> {
    const authToken: CookieKey = getCookie(CookieKey.AUTH_TOKEN);
    return webSocket(`${W2_WS_URL + endpointUrl}?authToken=${encodeURIComponent(authToken)}`);
  }

  private _getApiUrl(apiVer) {
    return `${API_URL}/api/${apiVer}`;
  }

  private isRequestAllowed(url): boolean {
    if (inSupportMode() && !SUPPORT_SESSION_ALLOWED_URLS.some((u: RegExp): boolean => url.match(u))) {
      return false;
    }
    if (inDemoMode() && !DEMO_SESSION_ALLOWED_URLS.some((u: RegExp): boolean => url.match(u))) {
      return false;
    }
    if (inReadOnlyMode() && !READONLY_MODE_ALLOWED_URLS.some((u: RegExp): boolean => url.match(u))) {
      return false;
    }
    return true;
  }

  private _getHeaders(headers = {}) {
    const defaults = clone(defaultHeaders);
    return new HttpHeaders(extend(defaults, headers));
  }

  private _getQueryParams(params, useBracketsForArrayValues: boolean = true) {
    let searchParams = new HttpParams({ encoder: this.customHttpParamCodec });
    if (!params) return searchParams;
    params.forEach((val, key) => {
      if (isArray(val)) {
        if (useBracketsForArrayValues) key = `${key}[]`;
        for (const v of val) searchParams = searchParams.append(key, v);
      } else searchParams = searchParams.set(key, val);
    });
    return searchParams;
  }

  private _getRequestOptions(
    methodType,
    params,
    headerParams = new Map(),
    useBracketsForArrayValues: boolean = true,
    observeEvents: boolean = false,
  ): any {
    let headers = {};

    if (!params) params = new Map();

    if (methodType === 'post' || methodType === 'put' || methodType === 'patch') {
      headers['Content-Type'] = 'application/json';
    }

    headers = this.setWebShareAPIKeyHeader(headers, headerParams);
    headers = this.setAcceptLanguageHeader(headers, headerParams);

    if (!params.has(GROUP_IDS_KEY)) {
      if (methodType === 'get' && this.groupsFilter) {
        const groupIds = this.groupsFilter.value || [];
        if (groupIds.length) {
          params.set(GROUP_IDS_KEY, groupIds);
        }
      }
    } else {
      if (!params.get(GROUP_IDS_KEY)) {
        params.delete(GROUP_IDS_KEY);
      }
    }

    if (observeEvents) {
      return {
        observe: 'events',
        reportProgress: true,
        headers: this._getHeaders(headers),
        params: this._getQueryParams(params, useBracketsForArrayValues),
      };
    } else {
      return {
        observe: 'response',
        headers: this._getHeaders(headers),
        params: this._getQueryParams(params, useBracketsForArrayValues),
      };
    }
  }

  private setWebShareAPIKeyHeader(requestHeaders: any, headerMap: Map<string, string>): any {
    const webShareApiKey = headerMap.get('X-Web-Share-Api-Key');
    if (webShareApiKey) {
      requestHeaders['X-Web-Share-Api-Key'] = webShareApiKey;
    }
    return requestHeaders;
  }

  private setAcceptLanguageHeader(requestHeaders: any, headerMap: Map<string, string>): any {
    const language: string = headerMap.get('Accept-Language');
    if (language) {
      requestHeaders['Accept-Language'] = language;
    }
    return requestHeaders;
  }

  private _checkVersion(res) {
    if (!res.headers) return;
    const newHardVersion = res.headers.get('X-Dashboard-Version');
    if (currentHardVersion && newHardVersion > currentHardVersion) {
      setCookie(HARD_VERSION_COOKIE_NAME, newHardVersion);
      // Defer until all microtasks have executed for reload to work correctly for non-GET requests
      const subscription = this.zone.onStable.subscribe(() => {
        subscription.unsubscribe();
        window.location.reload();
      });
    } else if (!currentHardVersion) {
      currentHardVersion = newHardVersion;
      setCookie(HARD_VERSION_COOKIE_NAME, newHardVersion);
    }
  }

  private requestDeniedError(): Observable<never> {
    displayRequestDeniedAlert();
    return observableThrowError(new Error(this.requestDeniedErrorMessage));
  }

  private decodeObfuscatedDotId(params: Map<string, string>): void {
    const dotIdKey: string = 'dot_id';
    if (params?.has(dotIdKey)) {
      params.set(dotIdKey, this.dotIdObfuscationService.decodeDotId(params.get(dotIdKey)).toString());
    }
  }
}
